import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/integrations/supabase/client';
import type {
  PluginCatalogEntry,
  PluginActivationResult,
  PluginDeactivationResult,
} from './types';

const pluginsQueryKey = (tenantId: string | undefined) => ['plugins', 'list', tenantId];

export function usePluginsList() {
  const { tenant } = useTenant();

  return useQuery({
    queryKey: pluginsQueryKey(tenant?.id),
    queryFn: async (): Promise<PluginCatalogEntry[]> => {
      const { data, error } = await supabase.rpc('plugins_list_for_tenant');
      if (error) throw error;
      return (data as PluginCatalogEntry[] | null) ?? [];
    },
    enabled: !!tenant?.id,
    staleTime: 1000 * 30,
  });
}

export function usePlugin(slug: string) {
  const { data: plugins, isLoading } = usePluginsList();
  const plugin = plugins?.find((p) => p.slug === slug);

  return {
    plugin,
    isLoading,
    isEnabled: plugin?.status === 'ativo' || plugin?.status === 'trial',
    config: (plugin?.config ?? {}) as Record<string, unknown>,
    activatedAt: plugin?.ativado_em ?? null,
  };
}

export function useActivatePlugin() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string): Promise<PluginActivationResult> => {
      const { data, error } = await supabase.rpc('plugins_activate', {
        p_plugin_slug: slug,
      });
      if (error) throw error;
      return data as PluginActivationResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pluginsQueryKey(tenant?.id) });
    },
  });
}

export function useDeactivatePlugin() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string): Promise<PluginDeactivationResult> => {
      const { data, error } = await supabase.rpc('plugins_deactivate', {
        p_plugin_slug: slug,
      });
      if (error) throw error;
      return data as PluginDeactivationResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pluginsQueryKey(tenant?.id) });
    },
  });
}

export function useUpdatePluginConfig() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      slug,
      config,
    }: {
      slug: string;
      config: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc('plugins_update_config', {
        p_plugin_slug: slug,
        p_config: config,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pluginsQueryKey(tenant?.id) });
    },
  });
}
